import { CdkDrag } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FeatureFlag } from '@core/enums';
import { NewPermission, Permission } from '@core/enums/permission';
import { FieldDef } from '@core/models';
import { NgxsOnInit, Select, Store } from '@ngxs/store';
import { ColumnSelectorModule } from '@shared/components/column-selector/column-selector.module';
import { FormFieldsModule } from '@shared/components/form-fields/form-fields.module';
import { RelatedToType } from '@shared/enums';
import { TaskState } from '@tasks/state/state';
import { AccountState } from '@state/account/state';
import { UserState } from '@state/user/state';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { Task, TaskList, TaskRelatedToModule } from '@tasks/tasks.types';
import { Observable } from 'rxjs';
import { TaskActions } from '@tasks/state/actions';
import { SelectableFieldDef } from '@shared/components/column-selector/column-selector.types';
import { TaskViewBase } from '@tasks/task-view-base';

@Component({
  selector: 'app-tssk-details-custom-fields',
  standalone: true,
  imports: [CommonModule, ColumnSelectorModule, MatTooltipModule, MatDividerModule, FormFieldsModule],
  template: `
    <ng-container *ngIf="taskCustomFields?.length">
      <div class="flex flex-row items-center mt-3">
        <p class="text-base font-medium pr-1">Custom fields</p>
        @if (canEditTaskLayout()) {
          <app-column-selector
            [matTooltip]="
              hasTaskLevelFields && (taskCustomFieldFeatureEnabled$ | async)
                ? 'Task-specific fields selected'
                : 'Select task list columns'
            "
            [items]="taskCustomFields"
            [selectedOrder]="selectedOrder"
            [draggable]="true"
            [disabled]="hasTaskLevelFields && (taskCustomFieldFeatureEnabled$ | async)"
            (onSave)="onSaveColumns($event)"
          ></app-column-selector>

          @if (!isAggregatedTask && taskCustomFieldFeatureEnabled$ | async) {
            <app-column-selector
              matTooltip="Select task columns"
              [items]="taskCustomFields"
              [selectedOrder]="selectedOrder"
              [draggable]="true"
              [icon]="{ name: 'playlist_add_check', isSvg: true }"
              (onSave)="onSaveColumns($event, false)"
              *ngIf="canEditTaskLayout"
            ></app-column-selector>
          }
        }
      </div>
      <mat-divider role="separator" class="mat-divider mt-1 pb-6 mat-divider-horizontal" aria-orientation="horizontal">
      </mat-divider>

      <ng-container *ngFor="let field of selectedFields">
        <ng-container *ngIf="lookupField(field)">
          <app-form-field
            [fieldDef]="field"
            [model]="task"
            (onUpdate)="customFieldUpdated($event)"
            [data]="data"
          ></app-form-field>
        </ng-container>
      </ng-container>
    </ng-container>
  `,
  styleUrl: './task-details-custom-fields.component.css',
})
export class TaskDetailsCustomFieldsComponent implements OnInit {
  store = inject(Store);
  taskCustomFields: FieldDef[] = [];
  taskFields: FieldDef[];
  selectedFields = [];
  selectedOrder = [];

  private _task: Task;
  @Input() set task(value: Task) {
    this._task = value;
    this.setupFields();
  }

  get task(): Task {
    return this._task;
  }

  private _taskList: TaskList;
  @Input() set taskList(value: TaskList) {
    this._taskList = value;
    this.setupFields();
  }

  get taskList(): TaskList {
    return this._taskList;
  }
  @Input() data;

  @Output() updateTask: EventEmitter<Task> = new EventEmitter<Task>();
  @Output() updateTaskList: EventEmitter<TaskList> = new EventEmitter<TaskList>();

  @Select(AccountState.isFeatureEnabled(FeatureFlag.TASKS_CUSTOM_FIELDS))
  taskCustomFieldFeatureEnabled$: Observable<boolean>;

  get hasTaskLevelFields(): boolean {
    return this.task?.definition?.dashboard?.fields?.length > 0;
  }

  get isAggregatedTask() {
    return (
      this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType.toLocaleLowerCase() === 'aggregatedtasks'
    );
  }

  ngOnInit() {
    this.setupFields();
  }

  canEditTaskLayout(): NewPermission | boolean {
    const relatedTo = this.store.selectSnapshot(TaskState.getTaskRelatedTo)?.relatedToType;
    if (!this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.store.selectSnapshot(UserState.hasPermission(Permission.TASK_LIST_EDIT_LAYOUT));
    } else {
      if (relatedTo === TaskRelatedToModule.Account) {
        return this.store.selectSnapshot(UserState.hasPermission(NewPermission.GLOBAL_TASK_LISTS_MANAGE_ADD_FORM));
      } else {
        return this.store.selectSnapshot(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_MANAGE_ADD_FORM));
      }
    }
  }

  onSaveColumns(selectedFieldDefs: SelectableFieldDef[], propagateToTaskList = true): void {
    this.selectedOrder = selectedFieldDefs.map(({ name }) => name) || [];
    const dashboard = {
      fields: this.selectedOrder,
    };

    if (propagateToTaskList) {
      this.updateTaskList.emit({
        ...this.taskList,
        definition: {
          ...this.taskList.definition,
          dashboard,
        },
      });
      this.refreshSelectedFields(selectedFieldDefs);
    } else {
      this.updateTask.emit({
        ...this.task,
        definition: {
          ...this.task?.definition,
          dashboard,
        },
      });

      this.setupFields();
    }
  }

  private refreshSelectedFields(selectedFieldDefs: SelectableFieldDef[]): void {
    this.selectedFields = selectedFieldDefs.filter((field) => field);

    this.taskCustomFields = [
      ...this.taskCustomFields.map((taskFieldDef: FieldDef) => {
        const selected = !!this.selectedFields.find(
          (selectedField: SelectableFieldDef) => selectedField.name === taskFieldDef.name,
        );

        return {
          ...taskFieldDef,
          selected,
        };
      }),
    ];

    this.selectedOrder = this.selectedFields.map(({ name }) => name) || [];
  }

  customFieldUpdated(params: { [key: string]: any }): void {
    this.updateTask.emit({ id: this.task.id, ...params });
  }

  private setupFields(): void {
    // should prioritize fields from task definition
    this.taskFields = this.store.selectSnapshot(
      ObjectsManagerState.getFieldsByObjectName('Task', {
        allowedFieldsOnly: true,
        addNestedFields: false,
      }),
    );
    this.taskCustomFields = this.taskFields.filter((field: FieldDef) => field.meta?.is_custom);

    if (
      this.hasTaskLevelFields &&
      this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.TASKS_CUSTOM_FIELDS))
    ) {
      this.setupTaskLevelFields();
    } else {
      // should fallback to tasks on the task list level
      this.setupTaskListLevelFields();
    }
  }

  private setupTaskLevelFields() {
    const [firstItem] = this.task.definition.dashboard.fields;

    const fields =
      typeof firstItem === 'string'
        ? this.task.definition.dashboard.fields
        : this.task.definition.dashboard.fields.map((field: any) => field.name);

    this.refreshSelectedFields(fields.map((field: string) => this.lookupFieldByName(field)) as SelectableFieldDef[]);
  }

  private setupTaskListLevelFields() {
    if (this.taskList?.definition?.dashboard?.fields) {
      const [firstItem] = this.taskList.definition?.dashboard?.fields;

      const fields =
        typeof firstItem === 'string'
          ? this.taskList.definition?.dashboard?.fields
          : this.taskList.definition?.dashboard?.fields.map((field: any) => field.name);

      this.refreshSelectedFields(fields.map((field: string) => this.lookupFieldByName(field)) as SelectableFieldDef[]);
    } else {
      this.refreshSelectedFields(this.taskCustomFields.map((field) => field) as SelectableFieldDef[]);
    }
  }

  lookupFieldByName(name: string): FieldDef | undefined {
    return this.taskFields?.find((fieldDef: FieldDef) => fieldDef.name === name);
  }

  lookupField(fieldDef: FieldDef): FieldDef {
    const customFieldValue = this.task?.custom_field_values?.find((fieldValue) => fieldValue?.name === fieldDef.name);
    const customFieldDef = this.taskFields.find((x) => x.name === fieldDef.name);
    return { ...customFieldDef, ...customFieldValue };
  }
}
